import { useEffect, useRef, useCallback } from 'react';

interface UseScrollSpyOptions {
  /** The scrollable container element ref. If null, observer uses viewport. */
  scrollRef: React.RefObject<HTMLElement | null>;
  /** Ref to the pill navigation bar (for auto-scroll of active pill). */
  navRef: React.RefObject<HTMLElement | null>;
  /** Category IDs to observe. */
  categoryIds: string[];
  /** Prefix for element IDs, e.g. 'spd-cat-' → looks for id="spd-cat-{catId}" */
  idPrefix: string;
  /** Whether the spy is active. */
  enabled: boolean;
  /** Temporarily suppress observer-driven active state changes. */
  paused?: boolean;
  /** Callback when active category changes. */
  onActiveCatChange: (catId: string) => void;
}

/**
 * Reusable ScrollSpy hook — uses IntersectionObserver to detect which category
 * section is crossing the activation band near the top of the scroll container,
 * auto-highlights the corresponding pill, and auto-scrolls the pill bar.
 */
export function useScrollSpy({
  scrollRef,
  navRef,
  categoryIds,
  idPrefix,
  enabled,
  paused = false,
  onActiveCatChange,
}: UseScrollSpyOptions) {
  // Keep callback ref fresh so scroll handler never captures stale closure
  const cbRef = useRef(onActiveCatChange);
  cbRef.current = onActiveCatChange;

  const idsRef = useRef(categoryIds);
  idsRef.current = categoryIds;

  const prefixRef = useRef(idPrefix);
  prefixRef.current = idPrefix;

  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    if (!enabled || !categoryIds.length) return;
    const rootEl = scrollRef.current;
    const root = rootEl && rootEl.scrollHeight > rootEl.clientHeight + 1 ? rootEl : null;

    const getSectionElement = (catId: string) => document.getElementById(`${prefixRef.current}${catId}`) as HTMLElement | null;
    const getRootRect = () => root
      ? root.getBoundingClientRect()
      : { top: 0, bottom: window.innerHeight, height: window.innerHeight };

    const pickNearestSection = () => {
      if (pausedRef.current) return;

      const ids = idsRef.current;
      const rootRect = getRootRect();
      const activationLine = rootRect.top + rootRect.height * 0.2;

      let bestId: string | null = null;
      let bestDistance = Number.POSITIVE_INFINITY;

      for (const id of ids) {
        const el = getSectionElement(id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.bottom <= activationLine) continue;
        if (rect.top >= rootRect.bottom) continue;

        const distance = Math.abs(rect.top - activationLine);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestId = id;
        }
      }

      if (bestId) cbRef.current(bestId);
    };

    const observer = new IntersectionObserver((entries) => {
      if (pausedRef.current) return;

      const rootRect = getRootRect();
      const activationLine = rootRect.top + rootRect.height * 0.2;
      const visible = entries
        .filter(entry => entry.isIntersecting)
        .map(entry => {
          const target = entry.target as HTMLElement;
          return {
            catId: target.dataset.sectionId || '',
            ratio: entry.intersectionRatio,
            top: entry.boundingClientRect.top,
          };
        })
        .filter(entry => entry.catId);

      if (visible.length === 0) {
        pickNearestSection();
        return;
      }

      visible.sort((a, b) => {
        const byDistance = Math.abs(a.top - activationLine) - Math.abs(b.top - activationLine);
        if (byDistance !== 0) return byDistance;
        return b.ratio - a.ratio;
      });

      cbRef.current(visible[0].catId);
    }, {
      root,
      threshold: [0, 0.2, 0.3],
      rootMargin: '-20% 0px -60% 0px',
    });

    const sections = categoryIds
      .map(catId => getSectionElement(catId))
      .filter((section): section is HTMLElement => Boolean(section));

    sections.forEach(section => observer.observe(section));
    pickNearestSection();

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, paused, categoryIds.join(','), idPrefix, scrollRef.current]);

  // Auto-scroll the active pill into view in the navigation bar
  const scrollPillIntoView = useCallback((activeCat: string) => {
    const nav = navRef.current;
    if (!nav || !activeCat) return;
    const pill = nav.querySelector(`[data-pill="${activeCat}"]`) as HTMLElement | null;
    if (pill) pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [navRef]);

  return { scrollPillIntoView };
}
